import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'app/utils/colors';
import { updatePassword } from 'app/services/company/userService';

const getToken = () => localStorage.getItem('access_token');

export default function ChangePassword({ show, onClose }) {
  const { t } = useTranslation('settings');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetAndClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setLoading(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    setError('');

    if (newPassword.length < 6) {
      setError(t('passwordChange.validation.passwordMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordChange.validation.confirmMismatch'));
      return;
    }

    setLoading(true);
    try {
      await updatePassword({ currentPassword, newPassword });
      resetAndClose();
    } catch (err) {
        if (err.response?.status === 400) {
          setError(t('passwordChange.validation.currentPasswordIncorrect'));
        } else {
          setError(t('passwordChange.alerts.changeError'));
        }
      } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={styles.overlay} onClick={resetAndClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div style={styles.header}>
          <h2 style={styles.title}>{t('passwordChange.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={resetAndClose}
            style={styles.closeBtn}
          >
            ×
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={styles.scrollArea}>
          <div style={styles.body}>
            <div style={styles.infoBox}>{t('passwordChange.infoBox')}</div>

            <label style={styles.label}>{t('passwordChange.fields.currentPassword')}</label>
            <input
              type="password"
              placeholder={t('passwordChange.placeholders.currentPassword')}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={styles.input}
            />

            <label style={styles.label}>{t('passwordChange.fields.newPassword')}</label>
            <input
              type="password"
              placeholder={t('passwordChange.placeholders.newPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.input}
            />

            <label style={styles.label}>{t('passwordChange.fields.confirmPassword')}</label>
            <input
              type="password"
              placeholder={t('passwordChange.placeholders.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
            />

            {error && <p style={styles.error}>{error}</p>}

            <button
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? t('passwordChange.actions.submitting') : t('passwordChange.actions.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px',
    background: '#f8f8f8',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '19px',
    fontWeight: '500',
    color: '#1a1a2e',
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: '1px solid #9ca3af',
    background: 'transparent',
    color: '#4b5563',
    fontSize: '16px',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
  },
  scrollArea: {
    overflowY: 'auto',
  },
  body: {
    padding: '24px 28px 28px',
  },
  infoBox: {
    background: '#f3f4f8',
    borderRadius: '6px',
    padding: '16px 18px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: colors.primary,
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    fontSize: '14px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    outline: 'none',
    marginBottom: '20px',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    background: '#fafafa',
    transition: 'border-color 0.2s',
  },
  btn: {
    width: '100%',
    padding: '13px',
    background: `${colors.primary}`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnDisabled: {
    background: '#a5b4fc',
    cursor: 'not-allowed',
  },
  error: {
    margin: '-8px 0 16px',
    padding: '10px 14px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#dc2626',
  },
};